import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../../../../shared/api/axiosInstance';
import type { ProductCategory, ProductCategoryTreeNode } from '../types';

interface ProductCategoryCreate {
  name: string;
  description?: string | null;
  image_id?: string | null;
  parent_id?: string | null;
}

interface ProductCategoryUpdate extends Partial<ProductCategoryCreate> {
  clear_image?: boolean;
  clear_parent?: boolean;
}

export const productCategoryKeys = {
  all: ['product-categories'] as const,
  list: (includeProducts?: boolean) =>
    [...productCategoryKeys.all, 'list', { includeProducts }] as const,
  tree: () => [...productCategoryKeys.all, 'tree'] as const,
  detail: (id: string) => [...productCategoryKeys.all, 'detail', id] as const,
};

export function useProductCategories(options?: { includeProducts?: boolean }) {
  const includeProducts = options?.includeProducts ?? false;
  return useQuery({
    queryKey: productCategoryKeys.list(includeProducts),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (includeProducts) {
        params.append('include_products', 'true');
      }
      const url = `/api/v1/master-data/products/categories${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await axiosInstance.get<{ data: ProductCategory[] }>(url);
      return response.data.data;
    },
  });
}

export function useProductCategoryTree() {
  return useQuery({
    queryKey: productCategoryKeys.tree(),
    queryFn: async () => {
      const response = await axiosInstance.get<{ data: ProductCategoryTreeNode[] }>(
        '/api/v1/master-data/products/categories/tree'
      );
      return response.data.data;
    },
  });
}

export function useProductCategory(id: string) {
  return useQuery({
    queryKey: productCategoryKeys.detail(id),
    queryFn: async () => {
      const response = await axiosInstance.get<{ data: ProductCategory }>(
        `/api/v1/master-data/products/categories/${id}`
      );
      return response.data.data;
    },
    enabled: !!id,
  });
}

export function useCreateProductCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: ProductCategoryCreate) => {
      const response = await axiosInstance.post<{ data: ProductCategory }>(
        '/api/v1/master-data/products/categories',
        data
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productCategoryKeys.all });
    },
  });
}

export function useUpdateProductCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ProductCategoryUpdate }) => {
      const response = await axiosInstance.put<{ data: ProductCategory }>(
        `/api/v1/master-data/products/categories/${id}`,
        data
      );
      return response.data.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: productCategoryKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: productCategoryKeys.all });
    },
  });
}

export function useDeleteProductCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await axiosInstance.delete(`/api/v1/master-data/products/categories/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productCategoryKeys.all });
    },
  });
}

export function useAddProductsToCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ categoryId, productIds }: { categoryId: string; productIds: string[] }) => {
      await axiosInstance.post(
        `/api/v1/master-data/products/categories/${categoryId}/products`,
        { product_ids: productIds }
      );
    },
    onSuccess: (_, { categoryId }) => {
      queryClient.invalidateQueries({ queryKey: productCategoryKeys.detail(categoryId) });
      queryClient.invalidateQueries({ queryKey: productCategoryKeys.all });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useRemoveProductFromCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ categoryId, productId }: { categoryId: string; productId: string }) => {
      await axiosInstance.delete(
        `/api/v1/master-data/products/categories/${categoryId}/products/${productId}`
      );
    },
    onSuccess: (_, { categoryId }) => {
      queryClient.invalidateQueries({ queryKey: productCategoryKeys.detail(categoryId) });
      queryClient.invalidateQueries({ queryKey: productCategoryKeys.all });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}
